import {
    MenuUnstyled,
    MenuUnstyledActions,
    PopperPlacementType,
} from '@mui/base';
import {
    MouseEvent,
    ReactNode,
    Ref,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { Icon } from '../icon';

import './MenuButton.scss';

export interface MenuButtonActions {
    close: () => void;
}

export interface MenuButtonProps {
    actions?: Ref<MenuButtonActions>;
    children?: ReactNode;
    classes?: string;
    icon?: ReactNode;
    id?: string;
    label: string;
    placement?: PopperPlacementType;
}

export const MenuButton = ({
    actions,
    children,
    classes,
    icon,
    id,
    label,
    placement,
}: MenuButtonProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const isOpen = Boolean(anchorEl);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuActions = useRef<MenuUnstyledActions>(null);
    const preventReopen = useRef(false);

    useImperativeHandle(actions, () => ({
        close,
    }));

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (preventReopen.current) {
            event.preventDefault();
            preventReopen.current = false;
            return;
        }

        if (isOpen) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleButtonMouseDown = () => {
        if (isOpen) {
            // Prevents the menu from reopening right after closing
            // when clicking the button.
            preventReopen.current = true;
        }
    };

    const handleButtonKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
            if (event.key === 'ArrowUp') {
                menuActions.current?.highlightLastItem();
            }
        }
    };

    const close = () => {
        setAnchorEl(null);
        buttonRef.current?.focus();
    };

    return (
        <>
            <button
                aria-controls={`${id}-menu`}
                id={`${id}-button`}
                aria-expanded={isOpen}
                aria-haspopup="true"
                className={`menu-button ${classes ?? ''}`}
                ref={buttonRef}
                onClick={handleButtonClick}
                onKeyDown={handleButtonKeyDown}
                onMouseDown={handleButtonMouseDown}
                type="button"
            >
                {icon}

                <span>{label}</span>

                <Icon name="chevron" aria-hidden="true" />
            </button>

            <MenuUnstyled
                actions={menuActions}
                anchorEl={anchorEl}
                onClose={close}
                open={isOpen}
                slotProps={{
                    root: {
                        className: 'menu-popover',
                        placement,
                    },
                    listbox: {
                        className: 'menu',
                        id: `${id}-menu`,
                        'aria-labelledby': `${id}-button`,
                    },
                }}
            >
                {children}
            </MenuUnstyled>
        </>
    );
};
